import { Button } from '../../components/Button';
import { Divider } from '../../components/Divider';
import { PageHeading } from '../../components/PageHeading';
import { PanelCard } from '../../components/PanelCard';
import { SectionHeader } from '../../components/SectionHeader';
import { SideImage } from '../../components/SideImage';
import { PreferencesPanel } from '../../composites/PreferencesPanel';
import { ReservationPanel } from '../../composites/ReservationPanel';
import { SplitLayout } from '../../composites/SplitLayout';
import { ChevronBackIcon } from '../../icons/ChevronBackIcon';
import { InfoIcon } from '../../icons/InfoIcon';
import {
  BackButton,
  BottomBar,
  BottomBarTitle,
  CardSection,
  Content,
  DepositBanner,
  EmptyTimeslots,
  FieldLabel,
  MenuCard,
  MenuLink,
  MenuLinks,
  Progress,
  ProgressDot,
  ProgressSegment,
  TimeslotGrid,
  RestaurantList,
  RestaurantName,
  RestaurantOption,
  RestaurantOptionText,
  RestaurantSubtitle,
  RestaurantThumb,
  RestaurantThumbPlaceholder,
  TopBar,
  TopBarSlot,
} from './DiningReservationTemplate.styles';
import type { DiningReservationTemplateProps } from './DiningReservationTemplate.types';

const noop = () => undefined;

export function DiningReservationTemplate({
  imageUrl,
  imageAlt,
  onBack,
  progressStep = 1,
  progressTotal = 2,
  restaurants,
  selectedRestaurantId,
  onSelectRestaurant,
  guestCount,
  minGuests,
  maxGuests,
  guestStep,
  onGuestCountChange,
  activeDate,
  selectedDate,
  calendarView,
  availability,
  onDateSelect,
  onWeekNavigate,
  onCalendarViewChange,
  timeslots,
  selectedTimeslot,
  onTimeslotSelect,
  menus,
  depositMessage,
  preferenceCategories,
  onPreferenceOptionToggle,
  onPreferenceOtherChange,
  onPreferenceFreeTextChange,
  onReserve,
  reserveDisabled,
  labels,
}: DiningReservationTemplateProps) {
  const selectedRestaurant = restaurants.find((r) => r.id === selectedRestaurantId) ?? null;
  const restaurantSelected = selectedRestaurant !== null;
  const dateSelected = restaurantSelected && Boolean(selectedDate);
  const hasTimeslots = Boolean(timeslots?.length);
  const showMenu = restaurantSelected && Boolean(menus?.length);
  const showPreferences = restaurantSelected && Boolean(preferenceCategories?.length);
  const canReserve = restaurantSelected && Boolean(selectedDate) && Boolean(selectedTimeslot);

  return (
    <SplitLayout
      leftWidth="45%"
      height="100vh"
      flushRightContent
      containedScroll
      left={<SideImage src={imageUrl} alt={imageAlt} sticky />}
      right={
        <>
          <TopBarSlot>
            <TopBar>
              {onBack && (
                <BackButton type="button" onClick={onBack} aria-label={labels.back ?? 'Back'}>
                  <ChevronBackIcon />
                </BackButton>
              )}
              <Progress>
                {Array.from({ length: progressTotal }).map((_, index) =>
                  index === 0 ? (
                    <ProgressSegment key={index} $active={index < progressStep} />
                  ) : (
                    <ProgressDot key={index} $active={index < progressStep} />
                  )
                )}
              </Progress>
            </TopBar>
          </TopBarSlot>

          <Content>
            <PageHeading title={labels.heading} subtitle={labels.subheading} />

            <PanelCard>
              <CardSection>
                <SectionHeader
                  title={labels.restaurantTitle}
                  subtitle={labels.restaurantSubtitle}
                />
                <RestaurantList>
                  {restaurants.map((restaurant) => (
                    <RestaurantOption
                      key={restaurant.id}
                      type="button"
                      $selected={restaurant.id === selectedRestaurantId}
                      aria-pressed={restaurant.id === selectedRestaurantId}
                      onClick={() => onSelectRestaurant(restaurant.id)}
                    >
                      <RestaurantOptionText>
                        <RestaurantName>{restaurant.name}</RestaurantName>
                        {restaurant.subtitle && (
                          <RestaurantSubtitle>{restaurant.subtitle}</RestaurantSubtitle>
                        )}
                      </RestaurantOptionText>
                      {restaurant.imageUrl ? (
                        <RestaurantThumb src={restaurant.imageUrl} alt={restaurant.name} />
                      ) : (
                        <RestaurantThumbPlaceholder aria-hidden />
                      )}
                    </RestaurantOption>
                  ))}
                </RestaurantList>
              </CardSection>

              {restaurantSelected && (
                <>
                  <Divider />
                  <CardSection>
                    <ReservationPanel
                      title={labels.reservationTitle}
                      subtitle={labels.reservationSubtitle}
                      guestLabel={labels.guestLabel}
                      guestCount={guestCount}
                      minGuests={minGuests}
                      maxGuests={maxGuests}
                      guestStep={guestStep}
                      onGuestCountChange={onGuestCountChange}
                      activeDate={activeDate}
                      selectedDate={selectedDate}
                      calendarView={calendarView}
                      availability={availability}
                      onDateSelect={onDateSelect}
                      onWeekNavigate={onWeekNavigate}
                      onCalendarViewChange={onCalendarViewChange}
                      calendarLabels={labels.calendar}
                      counterDecreaseLabel={labels.counter?.decrease}
                      counterIncreaseLabel={labels.counter?.increase}
                    />

                    {dateSelected && (
                      <>
                        <Divider />
                        <FieldLabel>{labels.timeLabel}</FieldLabel>
                        {hasTimeslots && timeslots ? (
                          <TimeslotGrid>
                            {timeslots.map((slot) => (
                              <Button
                                key={slot.value}
                                variant="toggle"
                                size="small"
                                fullWidth
                                selected={slot.value === selectedTimeslot}
                                onClick={() => onTimeslotSelect(slot.value)}
                                type="button"
                              >
                                {slot.label}
                              </Button>
                            ))}
                          </TimeslotGrid>
                        ) : (
                          <EmptyTimeslots>{labels.noTimeslotsLabel}</EmptyTimeslots>
                        )}
                      </>
                    )}

                    {(showMenu || depositMessage) && (
                      <MenuCard>
                        {showMenu && menus && (
                          <>
                            <FieldLabel>{labels.menuTitle}</FieldLabel>
                            <MenuLinks>
                              {menus.map((menu) => (
                                <MenuLink
                                  key={menu.id}
                                  href={menu.url}
                                  target="_blank"
                                  rel="noreferrer"
                                  aria-label={
                                    labels.menuTitle
                                      ? `${labels.menuTitle}: ${menu.title}`
                                      : menu.title
                                  }
                                >
                                  {menu.title}
                                </MenuLink>
                              ))}
                            </MenuLinks>
                          </>
                        )}
                        {depositMessage && (
                          <DepositBanner>
                            <InfoIcon />
                            {depositMessage}
                          </DepositBanner>
                        )}
                      </MenuCard>
                    )}
                  </CardSection>
                </>
              )}
            </PanelCard>

            {showPreferences && preferenceCategories && (
              <PanelCard>
                <CardSection $compact>
                  <PreferencesPanel
                    categories={preferenceCategories}
                    onOptionToggle={onPreferenceOptionToggle ?? noop}
                    onOtherChange={onPreferenceOtherChange ?? noop}
                    onFreeTextChange={onPreferenceFreeTextChange ?? noop}
                    otherButtonLabel={labels.preferences?.otherButtonLabel}
                  />
                </CardSection>
              </PanelCard>
            )}
          </Content>

          <BottomBar>
            <BottomBarTitle>{selectedRestaurant?.name ?? ''}</BottomBarTitle>
            <Button
              type="button"
              variant="primary"
              size="large"
              onClick={onReserve}
              disabled={(reserveDisabled ?? false) || !canReserve}
            >
              {labels.reserveLabel}
            </Button>
          </BottomBar>
        </>
      }
      contentGap="0px"
      contentMaxWidth="100%"
    />
  );
}
